/**
 * CR412: Sticky toolbar for Live Preview page
 * Consolidates view mode, search, category, preview settings, and action buttons.
 */

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SearchBarWithCategory } from "@/components/deals/SearchBarWithCategory";
import { FoldLineControl } from "@/components/deals/FoldLineControl";
import type { FoldLineHeight } from "@/components/deals/FoldLineOverlay";
import type { PreviewMode, PreviewDeal } from "@/hooks/useAdminPreviewDeals";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw,
  Rocket,
  Settings,
  ExternalLink,
  Layers,
  Shield,
  Globe,
  SlidersHorizontal,
} from "lucide-react";

interface LivePreviewToolbarProps {
  currentMode: PreviewMode;
  onModeChange: (mode: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedCategory: string;
  onCategoryChange: (c: string) => void;
  deals: PreviewDeal[];
  foldHeight: FoldLineHeight;
  onFoldHeightChange: (h: FoldLineHeight) => void;
  loading: boolean;
  stateLoading: boolean;
  onRefresh: () => void;
  // Staging batch picker
  selectedBatchId: string;
  onBatchChange: (id: string) => void;
  batchRuns: { id: string; status: string; deduped_count?: number | null; total_staged_items?: number | null }[];
}

export function LivePreviewToolbar({
  currentMode,
  onModeChange,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  deals,
  foldHeight,
  onFoldHeightChange,
  loading,
  stateLoading,
  onRefresh,
  selectedBatchId,
  onBatchChange,
  batchRuns,
}: LivePreviewToolbarProps) {
  return (
    <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b pb-3 pt-3 -mx-1 px-1 space-y-2">
      {/* Row 1: View Mode + Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Left: View Mode */}
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={currentMode} onValueChange={onModeChange}>
            <TabsList className="h-8">
              <TabsTrigger value="staging" className="gap-1 text-xs h-7 px-2.5">
                <Layers className="h-3 w-3" />
                Staging (Dedup)
              </TabsTrigger>
              <TabsTrigger value="admin" className="gap-1 text-xs h-7 px-2.5">
                <Shield className="h-3 w-3" />
                Admin (Prod)
              </TabsTrigger>
              <TabsTrigger value="live" className="gap-1 text-xs h-7 px-2.5">
                <Globe className="h-3 w-3" />
                Live (Public)
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Batch Picker (Staging only) */}
          {currentMode === "staging" && (
            <Select value={selectedBatchId} onValueChange={onBatchChange}>
              <SelectTrigger className="w-[260px] h-8 text-xs">
                <SelectValue placeholder="Select batch run..." />
              </SelectTrigger>
              <SelectContent>
                {batchRuns.map((run) => (
                  <SelectItem key={run.id} value={run.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{run.id.slice(0, 8)}</span>
                      <Badge variant="outline" className="text-[10px] h-4">{run.status}</Badge>
                      <span className="text-muted-foreground text-xs">
                        {run.deduped_count ?? run.total_staged_items ?? 0} deals
                      </span>
                    </div>
                  </SelectItem>
                ))}
                {batchRuns.length === 0 && (
                  <SelectItem value="__empty__" disabled>No batch runs</SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          {/* Preview Settings Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
                <SlidersHorizontal className="h-3 w-3" />
                Settings
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end">
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Preview Settings</h4>
                <FoldLineControl
                  value={foldHeight}
                  onChange={onFoldHeightChange}
                  showLabel={true}
                  compact={false}
                />
              </div>
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onRefresh} disabled={loading || stateLoading}>
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
            <Link to="/admin/batch-runs">
              <Rocket className="h-3 w-3 mr-1" />
              Batch Runs
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
            <Link to="/admin/command-center">
              <Settings className="h-3 w-3 mr-1" />
              Command Center
            </Link>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs" asChild>
            <a href="/" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3 w-3 mr-1" />
              Public Site
            </a>
          </Button>
        </div>
      </div>

      {/* Row 2: Search + Category */}
      <SearchBarWithCategory
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
        deals={deals}
      />
    </div>
  );
}
